import type { SupportedLanguage } from '../../../i18n';
import { DEFAULT_LANGUAGE } from '../../../i18n';

import zhCN from './zh-CN';
import zhTW from './zh-TW';
import en from './en';
import ja from './ja';
import ko from './ko';

const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

export const settingsTranslations = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  en,
  ja,
  ko,
} as const;

export type SettingsTranslations = typeof settingsTranslations;
export type SettingsTranslationDictionary = SettingsTranslations[keyof SettingsTranslations];

export const getSettingsTranslationDictionary = (
  language: SupportedLanguage,
): SettingsTranslationDictionary => {
  const dictionary = settingsTranslations[language as keyof SettingsTranslations];
  if (dictionary) {
    return dictionary;
  }

  return (
    settingsTranslations[DEFAULT_LANGUAGE as keyof SettingsTranslations] ??
    settingsTranslations[FALLBACK_LANGUAGE]
  );
};

const stripSettingsPrefix = (key: string): string => {
  if (key.startsWith('settings.')) {
    return key.substring('settings.'.length);
  }

  return key;
};

const resolveTranslationValue = (dictionary: Record<string, any>, key: string) => {
  const path = stripSettingsPrefix(key).split('.');
  let value: any = dictionary;

  for (const segment of path) {
    if (value && typeof value === 'object' && segment in value) {
      value = value[segment];
    } else {
      return undefined;
    }
  }

  return value;
};

const applyTemplate = (template: string, params?: Record<string, any>) => {
  if (!params) {
    return template;
  }

  return Object.keys(params).reduce((result, token) => {
    return result.replace(new RegExp(`\{${token}\}`, 'g'), String(params[token]));
  }, template);
};

export const translateSettings = (
  language: SupportedLanguage,
  key: string,
  params?: Record<string, any>,
): string => {
  const dictionary = getSettingsTranslationDictionary(language);
  const value = resolveTranslationValue(dictionary, key);

  if (typeof value !== 'string') {
    return key;
  }

  return applyTemplate(value, params);
};
